import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

import { lintHtmlComposition } from './lintHtmlComposition';
import { canvasSizeOf, captureFrames } from './measure';
import { runRuntimeLintRules } from './runtimeRules';
import type { LintFinding } from './types';

/**
 * Lint findings for the composition on `asset`: the static HTML-source rules, plus the runtime rules
 * run against the composition as luminary has it loaded.
 *
 * `sceneDurationMs` is the scene's real playback length in milliseconds (our default time unit); the
 * caller supplies it, because the timeline's own length is the authored one and need not match.
 * Rules needing a real duration no-op without it.
 */
export async function getCompositionLintFindings(
    asset: HtmlAsset,
    sceneDurationMs?: number,
): Promise<LintFinding[]> {
    const html = asset.html;
    if (!html) {
        return [];
    }
    const staticFindings = lintHtmlComposition(html);
    const runtimeFindings = await asset.introspectComposition(async (composition) => {
        const frames = await captureFrames(composition, composition.timeline ? undefined : [0]);
        return runRuntimeLintRules({
            document: composition.document,
            timeline: composition.timeline,
            sourceHtml: html,
            frames,
            canvasSize: canvasSizeOf(composition.document),
            sceneDurationMs,
            seek: composition.seek,
        });
    });
    return [...staticFindings, ...(runtimeFindings ?? [])];
}
